import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Check } from "lucide-react";
import { CTAButton } from "./CTAButton";
import { HeroVideo } from "./HeroVideo";
import { TRUST_ITEMS, IMAGES } from "@/data/content";
import { EASE } from "./Reveal";

const LINES = ["Geld verdienen mit", "Fahrzeug\u00ADüber\u00ADführungen", "– erfahre, wie es wirklich funktioniert."];

const MaskLine = ({ children, delay, gradient }) => (
  <span className="block overflow-hidden pb-[0.12em]">
    <motion.span
      className={`block ${gradient ? "text-gradient-brand" : "text-white"}`}
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 0.9, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section id="start" ref={ref} data-testid="hero" className="relative min-h-[100svh] overflow-hidden">
      {/* Parallax background */}
      <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0 -z-10">
        <img src={IMAGES.hero} alt="" className="h-full w-full object-cover" />
      </motion.div>
      <div className="absolute inset-0 -z-10 bg-gradient-to-b from-brand-bg/70 via-brand-bg/85 to-brand-bg" />
      <div className="absolute inset-0 -z-10 bg-[radial-gradient(120%_80%_at_15%_0%,rgba(30,155,255,0.18),transparent_55%)]" />

      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-5 pb-16 pt-28 lg:grid-cols-[1.05fr_0.95fr] lg:gap-10 lg:px-8 lg:pb-24 lg:pt-32">
        {/* Left: copy */}
        <div>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: EASE }}
            className="mb-6 inline-flex items-center gap-2 rounded-full border border-brand-green/40 bg-brand-green/10 px-4 py-1.5"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-brand-green animate-pulse-glow" />
            <span className="font-heading text-xs font-semibold uppercase tracking-[0.24em] text-brand-green">
              Kostenloses Online-Webinar
            </span>
          </motion.div>

          <h1 lang="de" className="font-heading text-[2.15rem] font-extrabold leading-[1.04] tracking-tight sm:text-6xl lg:text-[4.6rem]">
            <MaskLine delay={0.15}>{LINES[0]}</MaskLine>
            <MaskLine delay={0.3} gradient>{LINES[1]}</MaskLine>
            <span className="mt-1 block text-[1.35rem] font-semibold leading-[1.15] text-white/85 sm:text-3xl lg:text-4xl">
              <MaskLine delay={0.45}>{LINES[2]}</MaskLine>
            </span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.7, ease: EASE }}
            className="mt-6 max-w-xl font-body text-base leading-relaxed text-white/70 sm:text-lg"
          >
            Im kostenlosen Webinar erfährst du Schritt für Schritt, wie Fahrzeugüberführung
            funktioniert, welche Voraussetzungen du brauchst, wo du Aufträge findest und wie du
            damit flexibel Geld verdienen kannst.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.85, ease: EASE }}
            className="mt-8"
          >
            <CTAButton testId="hero-cta" className="w-full sm:w-auto" />
          </motion.div>

          <motion.ul
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 1, ease: EASE }}
            className="mt-6 grid grid-cols-2 gap-x-5 gap-y-2.5 sm:flex sm:flex-wrap sm:gap-x-6"
          >
            {TRUST_ITEMS.map((t) => (
              <li key={t} className="flex items-center gap-2 font-body text-sm text-white/75">
                <Check className="h-4 w-4 shrink-0 text-brand-green" strokeWidth={3} />
                {t}
              </li>
            ))}
          </motion.ul>
        </div>

        {/* Right: video placeholder */}
        <motion.div
          style={{ opacity: fade }}
          initial={{ opacity: 0, y: 40, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.9, delay: 0.5, ease: EASE }}
          className="relative"
        >
          <HeroVideo />
        </motion.div>
      </div>
    </section>
  );
};
